<template>
  <Section
    :id="`shipment-${shipment.id}-tracking-upper-section`"
    class="shipment-tracking-section"
  >
    <template #header>
      <div class="flex flex-gap-1 align-items-center">
        <img
          v-if="domainBranding?.logo?.signedUrl"
          :src="domainBranding.logo.signedUrl"
          width="100"
          height="100"
        />

        <div>
          <h2>{{ route }}</h2>
          <p>{{ shipment.carrier?.name }}</p>
          <p>
            {{
              t('shipments.shipmentNumParam', {
                param: shipment.id,
              })
            }}
          </p>
        </div>
      </div>

      <div
        v-if="contact?.phone || contact?.email"
        class="flex flex-gap-1 justify-content-end align-items-center"
      >
        <div>
          <h2>{{ t('shipments.contactUs') }}</h2>
          <p v-if="contact?.phone">{{ contact?.phone }}</p>
          <p v-if="contact?.email">{{ contact.email }}</p>
        </div>
      </div>
    </template>

    <div class="flex flex-column flex-gap-1 align-items-start">
      <p class="font-bold">
        {{ primaryAppointment.label }}
      </p>
      <div class="flex flex-gap-1 align-items-center">
        <span class="text-gray-darkest text-xl font-bold">
          {{ formattedPrimaryAppointment }}
        </span>
        <RygenChip
          v-if="isDelayed"
          class="delayed-chip"
          :label="t('orders.delayed')"
          :severity="Severity.WARNING"
        />
      </div>
      <p class="text-gray-darker font-bold">
        {{ formattedStatus }}
      </p>
    </div>

    <ShipmentProgressIndicators :shipment="shipment" />

    <ExpandButton
      v-model="showReferenceNumbers"
      mode="tertiary"
      class="reference-number-expand-button text-lg section-subheader"
      icon-pos="right"
      :true-label="t('referenceData.referenceNumbers', 2)"
      :false-label="t('referenceData.referenceNumbers', 2)"
      :target-id="`reference-number-data-table-${shipment.id}`"
    />

    <div v-if="showReferenceNumbers" class="tracking-header mt-0">
      <DataTable
        :id="`reference-number-data-table-${shipment.id}`"
        class="no-header"
        :value="shipment.referenceNumbers"
        tabindex="-1"
        striped-rows
      >
        <Column field="typeName" />
        <Column field="value" />
      </DataTable>
    </div>
  </Section>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { PropType } from 'vue';
import { computed, ref, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime, translateEnum } from '@rygen/rygen-ui/i18n';
import { DomainBranding, Severity } from '@rygen/rygen-ui/model';

import { renderCityState, useShippingAppointments } from '@/composables';
import { Contact, ShipmentStatus, ShipmentTrackingSummary } from '@/model';

import ShipmentProgressIndicators from '@/components/shipment/tracking/ShipmentProgressIndicators.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';

const props = defineProps({
  shipment: {
    type: Object as PropType<ShipmentTrackingSummary>,
    default: () => new ShipmentTrackingSummary(),
  },
  domainBranding: {
    type: Object as PropType<DomainBranding>,
    default: () => new DomainBranding(),
  },
  contact: {
    type: Object as PropType<Contact>,
    default: () => new Contact(),
  },
});
const { shipment, domainBranding, contact } = toRefs(props);

const { t } = useI18n();
const { primaryAppointment } = useShippingAppointments(
  toRef(() => shipment.value.destination),
);

const showReferenceNumbers = ref(false);

const status = computed(() => shipment.value.status ?? ShipmentStatus.PLANNING);

const formattedStatus = computed(() => {
  return translateEnum(status.value, 'ShipmentStatus');
});

const origin = computed(() => shipment.value.stops[0]);

const destination = computed(
  () => shipment.value.stops[shipment.value.stops.length - 1],
);

const route = computed(() => {
  return t('shipments.originParamToDestinationParam', {
    origin: renderCityState(origin.value.location.toAddress()),
    destination: renderCityState(destination.value.location.toAddress()),
  });
});

const expectedDeliveryDate = computed(() => destination.value.expectedArrival);

const formattedPrimaryAppointment = computed(() => {
  return primaryAppointment.value.date
    ? t('events.dateByTime', {
        date: renderDateTime(primaryAppointment.value.date, {
          timeFormat: 'NONE',
        }),
        time: renderDateTime(primaryAppointment.value.date, {
          dateFormat: 'NONE',
        }),
      })
    : t('common.unknown');
});

const isDelayed = computed(() => {
  return expectedDeliveryDate.value
    ? expectedDeliveryDate.value < DateTime.now()
    : false;
});
</script>

<style>
.shipment-tracking-section {
  &.detail-section {
    .section-header {
      --section-header-margin: 0 0 var(--sp-6) 0;
    }
  }

  .section-data {
    .delayed-chip.p-chip {
      background-color: var(--warning-light-color);
      color: var(--warning-dark-color);
    }

    .shipment-progress-indicators {
      margin: var(--sp-6) 0 var(--sp-9);
    }

    .reference-number-expand-button {
      color: var(--p-gray-600);

      .p-button-label {
        font-weight: 500;
      }
    }

    .p-datatable {
      &.no-header {
        .p-datatable-wrapper table thead tr {
          display: none;
        }
      }

      &.p-datatable-striped {
        .p-datatable-tbody {
          > tr:nth-child(even) {
            background: #ffffff;
          }
        }
      }

      .p-datatable-tbody {
        & > tr {
          background: var(--p-gray-100);
        }
      }
    }
  }
}
</style>
